import { DateTime } from "luxon";

/*
 * Util | Time Inputs
 */

const whiteSpaceRegex = /\s/;
const whiteSpaceRegexGlobal = /\s/g;

export const stripSpaces = (s: string): string => {
  return s.replaceAll(whiteSpaceRegexGlobal, "");
};

export const hasWhiteSpace = (s: string): boolean => {
  return whiteSpaceRegex.test(s);
};

export function convertDateTimeToTimeString(dateTime: DateTime) {
  return (
    dateTime
      // "12:00 AM"
      .toFormat("h:mm a")
      // "12:00 am"
      .toLowerCase()
  );
}

/*
 * Regex that accepts either a hh:mmTT or hh:mm TT, TT being the meridiem (am/pm).
 *
 * Both inputs with and without a leading hour zero are okay.
 */
const DTWithMeridiemRegex = /(([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])\s?([AaPp][Mm])?)/;

export const separateTimeAndMeridiem = (timeStr: string): string => {
  const isValidDTString = DTWithMeridiemRegex.test(timeStr);
  if (!isValidDTString) {
    // This code should never be reached without a prior check, so throw here.
    throw new Error("separateTimeAndMeridiem is being called with an invalid time.");
  }
  // Don't do anything if user inputs a AM/PM separated string
  if (hasWhiteSpace(timeStr)) return timeStr;
  // Add space in front of the second to last character
  return timeStr.replace(/.{2}$/, " $&");
};

/*
 * Takes in a time string in the format hh:mmTT or hh:mm TT, and a DateTime Object.
 *
 * The time string is first validated to ensure it has the correct format, then a
 * whitespace is added if it doesn't exist in front of the meridiem (AM/PM) since Luxon
 * requires this whitespace.
 *
 * The DateTime object's hour and minute are then updated to use these values.
 */

type ConvertStringToDTProps = {
  timeStr: string;
  dtObj: DateTime;
  min?: DateTime;
  max?: DateTime;
};

export const convertStringToDT = (props: ConvertStringToDTProps): DateTime | undefined => {
  const { timeStr, dtObj, min, max } = props;

  // Ensures that min will never exceed max
  if (min && max) {
    if (min > max) {
      throw new Error(
        "TimeInput: min time should not exceed max time! Double check to make sure that you are sending the right times",
      );
    } else if (max < min) {
      throw new Error(
        "TimeInput: max time should not happen before min time! Double check to make sure that you are sending the right times",
      );
    }
  }

  const isValidDTString = DTWithMeridiemRegex.test(timeStr);
  if (!isValidDTString) return;
  const validatedDT = DateTime.fromFormat(separateTimeAndMeridiem(timeStr), "t");
  if (!validatedDT.isValid) return;

  // if value happens to be set out of bounds, return undefined
  // this will then reset the value in the client
  if (min && validatedDT < min) {
    return;
  }
  if (max && validatedDT > max) {
    return;
  }

  return dtObj.set({ hour: validatedDT.hour, minute: validatedDT.minute });
};
